import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const line1 = "Expert fitness";
const line2 = "support.";
const line3 = "One rep";
const line4 = "at a time.";

function MaskedLine({ text, delay = 0, className = "" }) {
  return (
    <span className="mask-reveal block">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.05, ease: [0.22, 1, 0.36, 1], delay }}
        className={`block ${className}`}
      >
        {text}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section ref={ref} className="relative min-h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <motion.div
          style={{ y: imgY, scale: imgScale }}
          className="absolute right-0 top-0 h-full w-full md:w-[62%] lg:w-[55%]"
        >
          <div className="relative h-full w-full hero-image-mask">
            <img
              src="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
              alt="Athlete preparing to lift"
              className="h-full w-full object-cover object-center grayscale"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#050510]/10 to-[#050510]" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050510] via-transparent to-transparent" />
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="hidden md:flex absolute top-24 right-12 z-10 items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-white/50"
      >
        <span>01 / Manifesto</span>
        <span className="w-8 h-px bg-white/20" />
        <span>{new Date().getFullYear()}</span>
      </motion.div>

      <motion.div
        style={{ y: contentY }}
        className="relative z-10 min-h-[100svh] flex flex-col justify-end pb-16 md:pb-24 px-6 md:px-12 max-w-[1600px] mx-auto"
      >
        <h1
          className="font-anton uppercase leading-[0.86] tracking-tight text-white text-[16vw] md:text-[10vw] lg:text-[9vw]"
          data-testid="hero-headline"
        >
          <MaskedLine text={line1} delay={0.35} />
          <MaskedLine text={line2} delay={0.45} className="text-[#2A5BFF]" />
          <MaskedLine text={line3} delay={0.6} />
          <MaskedLine text={line4} delay={0.7} />
        </h1>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="md:col-span-6 lg:col-span-5 text-white/70 text-lg md:text-xl font-light leading-relaxed max-w-xl"
            data-testid="hero-subtext"
          >
            BRep connects you with qualified coaches for personalised, one-off fitness services — from a single lift review to a full custom training programme.
            <span className="block mt-3 text-white">Pay per service. No subscription. Real experts.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="md:col-span-6 lg:col-span-7 flex flex-col sm:flex-row items-start sm:items-center gap-4 md:justify-end"
          >
            <Link
              to="/services"
              className="btn-primary group inline-flex items-center gap-3 px-6 py-4 text-[12px] tracking-[0.22em] uppercase font-medium"
              data-testid="hero-cta-services"
            >
              View Services
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/download"
              className="btn-ghost group inline-flex items-center gap-3 px-6 py-4 text-[12px] tracking-[0.22em] uppercase font-medium"
              data-testid="hero-cta-download"
            >
              Download BRep
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-14 md:mt-20 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 text-[11px] tracking-[0.28em] uppercase text-white/50"
        >
          <span>Every Rep. <span className="text-[#2A5BFF]">Better.</span></span>
          <span className="hidden md:inline">Powered by <span className="text-white/80">Byron Performance Coaching</span></span>
          <span>Scroll ↓</span>
        </motion.div>
      </motion.div>
    </section>
  );
}
